<!-- eslint-disable no-trailing-spaces -->
<template>
  <div
    aria-label="Run sheet list"
    role="main"
  >
    <!-- Enhanced Filter Section -->
    <b-card class="mb-3">
      <template #header>
        <h5
          id="roadmap-list-title"
          class="mb-0"
        >
          Run sheet list
        </h5>
      </template>

      <!-- Status Filters Row -->
      <b-row class="mb-3 mt-1">
        <b-col md="6">
          <label class="mr-2 h6">Run sheet status:</label>
          <b-form-radio-group
            v-model="filters.is_payed"
            @input="applyFilters"
          >
            <b-form-radio :value="false">
              Non payé
            </b-form-radio>
            <b-form-radio :value="true">
              Payé
            </b-form-radio>
            <b-form-radio :value="null">
              Tous
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
        <b-col md="4">
          <label class="mr-2 h6">Validation:</label>
          <b-form-radio-group
            v-model="filters.is_validated"
            @input="applyFilters"
          >
            <b-form-radio :value="false">
              Non validé
            </b-form-radio>
            <b-form-radio :value="true">
              Validé
            </b-form-radio>
            <b-form-radio :value="null">
              Tous
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
        <b-col md="2">
          <label class="mr-2 h6">Terminé:</label>
          <b-form-radio-group
            v-model="filters.is_completed"
            @input="applyFilters"
          >
            <b-form-radio :value="false">
              Non
            </b-form-radio>
            <b-form-radio :value="true">
              Oui
            </b-form-radio>
            <b-form-radio :value="null">
              Tous
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
      </b-row>

      <!-- Search and Selection Filters Row -->
      <b-row>
        <b-col md="3">
          <b-form-group
            label="Run sheet number"
            label-for="roadmap_id"
          >
            <b-form-input
              v-model="filters.id"
              v-debounce:1000="applyFilters"
              placeholder="Recherche par ID"
              type="number"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Livreur">
            <v-select
              v-model="filters.delivery_man"
              :clearable="true"
              placeholder="Sélectionner un livreur"
              label="username"
              :options="deliveryMans"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Véhicule">
            <v-select
              v-model="filters.car"
              :clearable="true"
              placeholder="Sélectionner un véhicule"
              label="display_name"
              :options="cars"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="user.role === 'admin'"
          md="3"
        >
          <b-form-group label="Dépôt">
            <v-select
              v-model="filters.repository"
              :clearable="true"
              placeholder="Sélectionner un dépôt"
              label="name"
              :options="repositories"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Date Filters Row -->
      <b-row>
        <b-col md="3">
          <b-form-group label="Date de création (du)">
            <b-form-datepicker
              v-model="filters.created_at_from"
              v-bind="datePickerConfig"
              placeholder="Date de début"
              reset-button
              close-button
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Date de création (au)">
            <b-form-datepicker
              v-model="filters.created_at_to"
              v-bind="datePickerConfig"
              placeholder="Date de fin"
              reset-button
              close-button
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Prix total minimum">
            <b-form-input
              v-model="filters.total_price_min"
              v-debounce:1000="applyFilters"
              placeholder="Prix minimum"
              type="number"
              step="0.001"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Prix total maximum">
            <b-form-input
              v-model="filters.total_price_max"
              v-debounce:1000="applyFilters"
              placeholder="Prix maximum"
              type="number"
              step="0.001"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Action Buttons Row -->
      <b-row>
        <b-col
          md="12"
          class="d-flex align-items-end"
        >
          <b-form-group class="d-flex w-100">
            <b-button
              :to="{ name: 'roadmap-add' }"
              variant="gradient-primary"
              class="mr-2"
            >
              <feather-icon icon="PlusIcon" />
              <span class="ml-1">Ajouter</span>
            </b-button>
            <b-button
              variant="dark"
              class="mr-2 btn-black"
              @click="openTodayStatistics"
            >
              <feather-icon icon="TrendingUpIcon" />
              <span class="ml-1">Statistiques du jour</span>
            </b-button>
            <b-button
              variant="outline-secondary"
              @click="resetFilters"
            >
              <feather-icon icon="RefreshCwIcon" />
              <span class="ml-1">Réinitialiser</span>
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Applied Filters Display -->
    <div
      v-if="hasActiveFilters"
      class="mb-3"
    >
      <h6>Filtres appliqués:</h6>
      <div class="d-flex flex-wrap">
        <b-badge
          v-for="filter in appliedFilters"
          :key="filter.key"
          variant="primary"
          class="mr-2 mb-2 p-2"
          style="cursor: pointer;"
          @click="removeFilter(filter.key)"
        >
          {{ filter.label }}: {{ filter.value }}
          <feather-icon
            icon="XIcon"
            size="14"
            class="ml-1"
          />
        </b-badge>
        <b-button
          variant="outline-danger"
          size="sm"
          class="mb-2"
          @click="clearAllFilters"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
          Effacer tous les filtres
        </b-button>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <!-- No Results Found -->
    <div
      v-if="!isLoading && roadmaps.length === 0"
      class="text-center py-5"
    >
      <b-card
        class="shadow-sm no-results-card"
        aria-live="polite"
      >
        <div
          class="text-center"
          aria-label="Aucun résultat trouvé"
        >
          <span class="sr-only">Aucun résultat trouvé pour les filtres appliqués.</span>
        </div>
        <div class="mb-3">
          <feather-icon
            icon="SearchIcon"
            size="48"
            class="text-muted"
          />
        </div>
        <h5 class="text-muted mb-3">
          Aucune run sheet trouvée
        </h5>
        <p class="text-muted mb-4">
          <span v-if="hasActiveFilters">
            Aucune run sheet ne correspond aux critères de recherche actuels.
            <br>
            Essayez de modifier ou supprimer certains filtres.
          </span>
          <span v-else>
            Il n'y a actuellement aucune run sheet dans le système.
          </span>
        </p>
        <div v-if="hasActiveFilters">
          <b-button
            variant="outline-primary"
            @click="clearAllFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            Réinitialiser les filtres
          </b-button>
        </div>
        <div v-else-if="user.role === 'admin' || user.role === 'manager'">
          <b-button
            :to="{ name: 'roadmap-add' }"
            variant="primary"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Créer votre première run sheet
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Results Table -->
    <vue-good-table
      v-if="!isLoading && roadmaps.length > 0"
      :columns="columns"
      :rows="roadmaps"
      :search-options="{ enabled: false }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <qrcode-vue
            style="display:inline"
            :value="props.row.id.toString()"
            :size="size"
            level="H"
          />
          <p class="ml-2">
            {{ props.row.id }}
          </p>
        </div>

        <!-- Column: Payment Status -->
        <span v-else-if="props.column.field === 'is_payed'">
          <span
            :class="['status-pill', props.row.is_payed ? 'pill-paid' : 'pill-unpaid']"
          >
            <feather-icon
              :icon="props.row.is_payed ? 'CheckIcon' : 'ClockIcon'"
              size="14"
              class="mr-50"
            />
            {{ props.row.is_payed ? 'Payé' : 'Non payé' }}
          </span>
          <div
            v-if="props.row.is_payed && (props.row.paid_at || props.row.statut_paiement)"
            class="small text-muted mt-1"
          >
            <span>
              payé le {{ formatDate(props.row.paid_at || props.row.statut_paiement) }}
            </span>
          </div>
        </span>

        <!-- Column: Validation Status -->
        <span v-else-if="props.column.field === 'is_validated'">
          <div>
            <span
              :class="['status-pill', props.row.is_validated ? 'pill-validated' : 'pill-unvalidated']"
            >
              <feather-icon
                :icon="props.row.is_validated ? 'CheckCircleIcon' : 'CircleIcon'"
                size="14"
                class="mr-50"
              />
              {{ props.row.is_validated ? 'Validé' : 'Non validé' }}
            </span>
            <div
              v-if="props.row.is_validated && props.row.validated_at"
              class="small text-muted mt-1"
            >
              <span>le {{ formatDate(props.row.validated_at) }}</span>
            </div>
          </div>
        </span>

        <!-- Column: Completion Status -->
        <span v-else-if="props.column.field === 'is_completed'">
          <div>
            <span
              :class="['status-pill', props.row.is_completed ? 'pill-completed' : 'pill-progress']"
            >
              <feather-icon
                :icon="props.row.is_completed ? 'CheckIcon' : 'ClockIcon'"
                size="14"
                class="mr-50"
              />
              {{ props.row.is_completed ? 'Terminé' : 'En cours' }}
            </span>
            
            <!-- Warning for untreated driver tickets -->
            <div
              v-if="!props.row.is_completed && props.row.is_validated && props.row.untreated_driver_tickets_count > 0"
              class="mt-1"
            >
              <span class="status-pill pill-warning">
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="12"
                  class="mr-50"
                />
                {{ props.row.untreated_driver_tickets_count }} réclamation(s)
              </span>
            </div>
            
            <div
              v-if="props.row.is_completed && (props.row.completed_at || props.row.completed_by)"
              class="small text-muted mt-1"
            >
              <span v-if="props.row.completed_at">le {{ formatDate(props.row.completed_at) }}</span>
              <span
                v-if="props.row.completed_by"
                class="d-block"
              >
                par {{ props.row.completed_by }}
              </span>
            </div>
          </div>
        </span>

        <!-- Column: Delivery Man -->
        <div
          v-else-if="props.column.field === 'delivery_man'"
          class="text-nowrap"
        >
          <b-avatar
            :src="props.row.avatar"
            class="mx-1"
          />
          <span class="text-nowrap">{{ props.row.delivery_man }}</span>
        </div>

        <!-- Column: Action -->
        <span v-else-if="props.column.field === 'action'">
          <span>
            <router-link
              class="text-info"
              :to="{ name: 'roadmap-show', params: { id: props.row.id} }"
            >
              <feather-icon
                :id="`roadmap-row-${props.row.id}-show-icon`"
                icon="EyeIcon"
                class="cursor-pointer mr-1"
                size="16"
              />
            </router-link>
            <b-tooltip
              title="Détail"
              class="cursor-pointer"
              :target="`roadmap-row-${props.row.id}-show-icon`"
            />

            <!-- Modern Show2 navigation icon -->
            <router-link
              class="text-primary"
              :to="{ name: 'roadmap-show-2', params: { id: props.row.id} }"
            >
              <feather-icon
                :id="`roadmap-row-${props.row.id}-show2-icon`"
                icon="LayoutIcon"
                class="cursor-pointer mr-1"
                size="16"
              />
            </router-link>
            <b-tooltip
              title="Vue moderne"
              class="cursor-pointer"
              :target="`roadmap-row-${props.row.id}-show2-icon`"
            />

            <!-- Show edit if not payed and (not validated OR user is admin) -->
            <router-link
              v-if="!props.row.is_payed && (!props.row.is_validated || user.role === 'admin')"
              class="text-warning"
              :to="{ name: 'roadmap-edit', params: { id: props.row.id} }"
            >
              <feather-icon
                :id="`roadmap-row-${props.row.id}-edit-icon`"
                icon="EditIcon"
                class="cursor-pointer mr-1"
                size="16"
              />
            </router-link>
            <b-tooltip
              v-if="!props.row.is_payed && (!props.row.is_validated || user.role === 'admin')"
              title="Modifier"
              class="cursor-pointer"
              :target="`roadmap-row-${props.row.id}-edit-icon`"
            />

            <!-- Validation Toggle Button: Only for admin users -->
            <feather-icon
              v-if="user.role === 'admin' && !props.row.is_validated"
              :id="`roadmap-row-${props.row.id}-validate-icon`"
              :icon="'ToggleLeftIcon'"
              :class="['cursor-pointer mr-1', 'text-secondary']"
              size="16"
              @click="openValidationConfirm(props.row)"
            />
            <b-tooltip
              v-if="user.role === 'admin' && !props.row.is_validated"
              :title="'Valider'"
              class="cursor-pointer"
              :target="`roadmap-row-${props.row.id}-validate-icon`"
            />

            <!-- Non-Validation Toggle Button: Only for admin users on validated roadmaps -->
            <feather-icon
              v-if="user.role === 'admin' && props.row.is_validated && !props.row.is_payed"
              :id="`roadmap-row-${props.row.id}-invalidate-icon`"
              :icon="'ToggleRightIcon'"
              :class="['cursor-pointer mr-1', 'text-warning']"
              size="16"
              @click="openNonValidationConfirm(props.row)"
            />
            <b-tooltip
              v-if="user.role === 'admin' && props.row.is_validated && !props.row.is_payed"
              :title="'Annuler la validation'"
              class="cursor-pointer"
              :target="`roadmap-row-${props.row.id}-invalidate-icon`"
            />

            <!-- Completion Toggle Button: For admin, manager, or support -->
            <feather-icon
              v-if="(user.role === 'admin' || user.role === 'manager' || user.role === 'customer_support') && props.row.is_validated"
              :id="`roadmap-row-${props.row.id}-complete-icon`"
              :icon="props.row.is_completed ? 'CheckSquareIcon' : 'SquareIcon'"
              :class="['cursor-pointer mr-1', props.row.is_completed ? 'text-success' : 'text-warning']"
              size="16"
              @click="openCompletionConfirm(props.row)"
            />
            <b-tooltip
              v-if="(user.role === 'admin' || user.role === 'manager' || user.role === 'customer_support') && props.row.is_validated"
              :title="props.row.is_completed ? 'Marquer comme non terminé' : 'Marquer comme terminé'"
              class="cursor-pointer"
              :target="`roadmap-row-${props.row.id}-complete-icon`"
            />
          </span>
        </span>

        <!-- Column: Date/Heure (formatted) -->
        <span v-else-if="props.column.field === 'created_at'">
          {{ formatDate(props.row.created_at) }}
        </span>
        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              Affichage 1 à
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value) => {
                props.perPageChanged({currentPerPage:value});
                applyFilters();
              }"
            />
            <span class="text-nowrap"> de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="pageNext(currentPage)"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Summary Card for Admin Users -->
    <b-card
      v-if="!isLoading && roadmaps.length > 0 && user.role === 'admin'"
      class="mt-3"
      header="Résumé des Run Sheets"
    >
      <b-row>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-primary">
              {{ count }}
            </h4>
            <p class="text-muted">
              Total Run sheets
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-success">
              {{ totalPaidRoadmaps }}
            </h4>
            <p class="text-muted">
              Feuilles payées
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-warning">
              {{ totalUnpaidRoadmaps }}
            </h4>
            <p class="text-muted">
              Feuilles non payées
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-info">
              {{ totalPrice }} DT
            </h4>
            <p class="text-muted">
              Total Prix
            </p>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Confirmation Modal for Validation Toggle -->
    <b-modal
      id="confirm-validation-modal"
      ref="confirmValidationModal"
      title="Valider la run sheet ?"
      ok-title="Valider et verrouiller"
      cancel-title="Annuler"
      centered
      ok-variant="success"
      cancel-variant="secondary"
      @ok="confirmToggleValidation"
    >
      <div class="text-center p-2">
        <feather-icon
          icon="LockIcon"
          size="32"
          class="mb-2 text-warning"
        />
        <h5 class="mb-2 font-weight-bold">
          Confirmer la validation de la run sheet
        </h5>
        <p class="mb-2 text-muted">
          <strong>Attention :</strong> Une fois la run sheet validée, <span class="text-danger">toute modification sera impossible</span>.<br>
          L'édition sera désactivée et aucun colis ne pourra être ajouté ou retiré de cette run sheet.<br>
          Cette action permet de <span class="text-primary">verrouiller la run sheet</span> pour garantir la traçabilité et la sécurité de vos expéditions.
        </p>
        <ul class="text-left small mb-2">
          <li>Vérifiez attentivement les informations de la run sheet avant de valider.</li>
          <li>La validation protège votre entreprise contre les comportements non professionnels ou les modifications après le départ du chauffeur.</li>
          <li>Ce verrouillage assure un suivi fiable des expéditions et des livraisons.</li>
        </ul>
        <p class="mb-0 font-italic text-info">
          Cette opération est irréversible. Êtes-vous certain de vouloir <span class="font-weight-bold">valider et verrouiller</span> cette run sheet ?
        </p>
      </div>
    </b-modal>

    <!-- Confirmation Modal for Completion Toggle -->
    <b-modal
      id="confirm-completion-modal"
      ref="confirmCompletionModal"
      :title="selectedRoadmapForCompletion && selectedRoadmapForCompletion.is_completed ? 'Marquer comme non terminé ?' : 'Marquer comme terminé ?'"
      :ok-title="selectedRoadmapForCompletion && selectedRoadmapForCompletion.is_completed ? 'Marquer non terminé' : 'Marquer terminé'"
      cancel-title="Annuler"
      centered
      :ok-variant="selectedRoadmapForCompletion && selectedRoadmapForCompletion.is_completed ? 'warning' : 'success'"
      cancel-variant="secondary"
      @ok="confirmToggleCompletion"
    >
      <div class="text-center p-2">
        <feather-icon
          :icon="selectedRoadmapForCompletion && selectedRoadmapForCompletion.is_completed ? 'AlertTriangleIcon' : 'CheckCircleIcon'"
          size="32"
          :class="['mb-2', selectedRoadmapForCompletion && selectedRoadmapForCompletion.is_completed ? 'text-warning' : 'text-success']"
        />
        <h5 class="mb-2 font-weight-bold">
          {{ selectedRoadmapForCompletion && selectedRoadmapForCompletion.is_completed ? 'Annuler le statut terminé' : 'Confirmer la fin de la tournée' }}
        </h5>
        <p class="mb-2 text-muted">
          <span v-if="selectedRoadmapForCompletion && !selectedRoadmapForCompletion.is_completed">
            <strong>Confirmer que :</strong> Le chauffeur a terminé sa tournée et est de retour au dépôt.<br>
            Une fois marquée comme terminée, la run sheet sera verrouillée et <span class="text-danger">aucune modification des commandes ne sera possible</span>.
          </span>
          <span v-else>
            <strong>Attention :</strong> Vous allez annuler le statut terminé de cette run sheet.<br>
            Cela permettra à nouveau les modifications des commandes par le chauffeur.
          </span>
        </p>
        <p class="mb-0 font-italic text-info">
          Êtes-vous certain de vouloir continuer ?
        </p>
      </div>
    </b-modal>

    <!-- Confirmation Modal for Non-Validation Toggle -->
    <b-modal
      id="confirm-non-validation-modal"
      ref="confirmNonValidationModal"
      title="Annuler la validation de la run sheet ?"
      ok-title="Annuler la validation"
      cancel-title="Annuler"
      centered
      ok-variant="warning"
      cancel-variant="secondary"
      @ok="confirmToggleNonValidation"
    >
      <div class="text-center p-2">
        <feather-icon
          icon="AlertTriangleIcon"
          size="32"
          class="mb-2 text-warning"
        />
        <h5 class="mb-2 font-weight-bold">
          Confirmer l'annulation de la validation
        </h5>
        <p class="mb-2 text-muted">
          <strong>Attention :</strong> Vous allez annuler la validation de cette run sheet.<br>
          Cela permettra à nouveau <span class="text-primary">les modifications de la run sheet</span> par les administrateurs et pourra affecter la traçabilité des expéditions.
        </p>
        <ul class="text-left small mb-2">
          <li>L'édition sera à nouveau possible pour les administrateurs.</li>
          <li>Le statut de validation sera remis à "Non validé".</li>
          <li>Cette action sera enregistrée dans l'historique.</li>
        </ul>
        <p class="mb-0 font-italic text-info">
          Êtes-vous certain de vouloir <span class="font-weight-bold">annuler la validation</span> de cette run sheet ?
        </p>
      </div>
    </b-modal>

    <!-- Today's Statistics Sidebar -->
    <b-sidebar
      id="today-statistics-sidebar"
      v-model="showStatisticsSidebar"
      title="Statistiques du Jour"
      right
      shadow
      width="700px"
      backdrop
    >
      <template #header="{ hide }">
        <div class="d-flex align-items-center justify-content-between w-100 p-3">
          <h4 class="mb-0">
            <feather-icon
              icon="TrendingUpIcon"
              size="24"
              class="mr-2 text-primary"
            />
            Tableau de Bord - Aujourd'hui
          </h4>
          <b-button
            variant="link"
            class="text-dark"
            @click="hide"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div class="px-3 pb-3">
        <!-- Loading State -->
        <div
          v-if="statisticsLoading"
          class="text-center py-5"
        >
          <b-spinner
            variant="primary"
            label="Chargement..."
          />
          <p class="mt-3 text-muted">
            Chargement des statistiques...
          </p>
        </div>

        <!-- Statistics Content -->
        <div v-else-if="todayStatistics">
          <!-- Date Badge -->
          <div class="mb-3">
            <b-badge
              variant="light-primary"
              class="p-2"
            >
              <feather-icon
                icon="CalendarIcon"
                size="14"
              />
              {{ formatStatisticsDate(todayStatistics.date) }}
            </b-badge>
          </div>

          <!-- Overview Cards -->
          <h5 class="mb-3">
            <feather-icon
              icon="PieChartIcon"
              size="18"
              class="mr-1"
            />
            Vue d'ensemble
          </h5>
          <b-row class="mb-4">
            <b-col
              cols="6"
              class="mb-3"
            >
              <b-card class="stat-card text-center border-primary">
                <feather-icon
                  icon="TruckIcon"
                  size="32"
                  class="text-primary mb-2"
                />
                <h3 class="font-weight-bold text-primary mb-0">
                  {{ todayStatistics.overview.total_roadmaps }}
                </h3>
                <p class="text-muted mb-0 small">
                  Run sheets
                </p>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              class="mb-3"
            >
              <b-card class="stat-card text-center border-success">
                <feather-icon
                  icon="PackageIcon"
                  size="32"
                  class="text-success mb-2"
                />
                <h3 class="font-weight-bold text-success mb-0">
                  {{ todayStatistics.overview.total_orders }}
                </h3>
                <p class="text-muted mb-0 small">
                  Colis total
                </p>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              class="mb-3"
            >
              <b-card class="stat-card text-center border-info">
                <feather-icon
                  icon="UsersIcon"
                  size="32"
                  class="text-info mb-2"
                />
                <h3 class="font-weight-bold text-info mb-0">
                  {{ todayStatistics.overview.active_drivers_count }}
                </h3>
                <p class="text-muted mb-0 small">
                  Livreurs actifs
                </p>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              class="mb-3"
            >
              <b-card class="stat-card text-center border-warning">
                <feather-icon
                  icon="TruckIcon"
                  size="32"
                  class="text-warning mb-2"
                />
                <h3 class="font-weight-bold text-warning mb-0">
                  {{ todayStatistics.overview.active_vehicles_count }}
                </h3>
                <p class="text-muted mb-0 small">
                  Véhicules en service
                </p>
              </b-card>
            </b-col>
          </b-row>

          <!-- Revenue and Averages -->
          <b-row class="mb-4">
            <b-col cols="12">
              <b-card class="bg-gradient-primary text-white">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 class="text-white mb-1">
                      Montant total à collecter
                    </h5>
                    <h2 class="font-weight-bold text-white mb-0">
                      {{ todayStatistics.overview.collectible_amount_total }} DT
                    </h2>
                  </div>
                  <feather-icon
                    icon="DollarSignIcon"
                    size="48"
                    class="text-white opacity-50"
                  />
                </div>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              class="mt-3"
            >
              <b-card class="text-center">
                <p class="text-muted mb-1 small">
                  Moyenne colis/feuille
                </p>
                <h4 class="font-weight-bold mb-0">
                  {{ todayStatistics.overview.avg_orders_per_roadmap }}
                </h4>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              class="mt-3"
            >
              <b-card class="text-center">
                <p class="text-muted mb-1 small">
                  Montant moyen à collecter/feuille
                </p>
                <h4 class="font-weight-bold mb-0">
                  {{ todayStatistics.overview.avg_collectible_amount_per_roadmap }} DT
                </h4>
              </b-card>
            </b-col>
          </b-row>

          <!-- Status Breakdown -->
          <h5 class="mb-3">
            <feather-icon
              icon="ActivityIcon"
              size="18"
              class="mr-1"
            />
            État d'avancement
          </h5>
          <b-card class="mb-4">
            <div class="mb-3">
              <div class="d-flex justify-content-between mb-2">
                <span>Validé</span>
                <span class="font-weight-bold">{{ todayStatistics.status_breakdown.validated }} / {{ todayStatistics.overview.total_roadmaps }}</span>
              </div>
              <b-progress
                :value="todayStatistics.status_breakdown.validation_rate"
                :max="100"
                variant="info"
                height="8px"
              />
              <small class="text-muted">{{ todayStatistics.status_breakdown.validation_rate }}%</small>
            </div>
            <div class="mb-3">
              <div class="d-flex justify-content-between mb-2">
                <span>Terminé</span>
                <span class="font-weight-bold">{{ todayStatistics.status_breakdown.completed }} / {{ todayStatistics.overview.total_roadmaps }}</span>
              </div>
              <b-progress
                :value="todayStatistics.status_breakdown.completion_rate"
                :max="100"
                variant="success"
                height="8px"
              />
              <small class="text-muted">{{ todayStatistics.status_breakdown.completion_rate }}%</small>
            </div>
            <div>
              <div class="d-flex justify-content-between mb-2">
                <span>Payé</span>
                <span class="font-weight-bold">{{ todayStatistics.status_breakdown.paid }} / {{ todayStatistics.overview.total_roadmaps }}</span>
              </div>
              <b-progress
                :value="todayStatistics.status_breakdown.payment_rate"
                :max="100"
                variant="warning"
                height="8px"
              />
              <small class="text-muted">{{ todayStatistics.status_breakdown.payment_rate }}%</small>
            </div>
          </b-card>

          <!-- Drivers Performance -->
          <h5 class="mb-3">
            <feather-icon
              icon="UsersIcon"
              size="18"
              class="mr-1"
            />
            Performance des livreurs
          </h5>
          <div
            v-if="todayStatistics.drivers.length > 0"
            class="mb-4"
          >
            <b-card
              v-for="driver in todayStatistics.drivers"
              :key="driver.id"
              class="mb-3"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 class="mb-1 font-weight-bold">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="mr-1"
                    />
                    {{ driver.username }}
                  </h6>
                  <small class="text-muted">
                    <feather-icon
                      icon="PhoneIcon"
                      size="12"
                    />
                    {{ driver.phone || 'N/A' }}
                  </small>
                </div>
                <b-badge
                  variant="light-primary"
                  class="p-2"
                >
                  {{ driver.roadmaps_count }} feuille(s)
                </b-badge>
              </div>
              
              <b-row class="mt-2">
                <b-col cols="4">
                  <small class="text-muted d-block">Colis</small>
                  <strong>{{ driver.orders_count }}</strong>
                </b-col>
                <b-col cols="8">
                  <small class="text-muted d-block">Montant à collecter</small>
                  <strong>{{ driver.collectible_amount_total }} DT</strong>
                </b-col>
              </b-row>
              
              <div class="mt-2">
                <small class="text-muted d-block mb-1">Véhicule(s) utilisé(s)</small>
                <div>
                  <b-badge
                    v-for="vehicle in driver.vehicles_used"
                    :key="vehicle"
                    variant="light-secondary"
                    class="mr-1 mb-1"
                  >
                    <feather-icon
                      icon="TruckIcon"
                      size="12"
                    />
                    {{ vehicle }}
                  </b-badge>
                  <b-badge
                    v-if="driver.vehicles_used.length === 0"
                    variant="light-secondary"
                  >
                    Aucun véhicule
                  </b-badge>
                </div>
                <small
                  v-if="driver.default_car && !driver.vehicles_used.includes(driver.default_car)"
                  class="text-warning d-block mt-1"
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="12"
                  />
                  Véhicule par défaut: {{ driver.default_car }}
                </small>
              </div>
              
              <div class="mt-2">
                <small class="text-muted">Feuilles: </small>
                <router-link
                  v-for="(roadmapId, index) in driver.roadmap_ids"
                  :key="roadmapId"
                  :to="{ name: 'roadmap-show', params: { id: roadmapId } }"
                  class="text-primary"
                >
                  #{{ roadmapId }}<span v-if="index < driver.roadmap_ids.length - 1">, </span>
                </router-link>
              </div>
            </b-card>
          </div>
          <b-alert
            v-else
            variant="info"
            show
          >
            <feather-icon
              icon="InfoIcon"
              class="mr-1"
            />
            Aucun livreur actif aujourd'hui
          </b-alert>

          <!-- Repositories Performance -->
          <h5
            v-if="todayStatistics.repositories.length > 0"
            class="mb-3"
          >
            <feather-icon
              icon="HomeIcon"
              size="18"
              class="mr-1"
            />
            Performance des dépôts
          </h5>
          <div
            v-if="todayStatistics.repositories.length > 0"
            class="mb-4"
          >
            <b-card
              v-for="repo in todayStatistics.repositories"
              :key="repo.id"
              class="mb-2"
            >
              <b-row class="align-items-center">
                <b-col cols="6">
                  <h6 class="mb-1 font-weight-bold">
                    {{ repo.name }}
                  </h6>
                  <small class="text-muted">{{ repo.roadmaps_count }} feuille(s)</small>
                </b-col>
                <b-col cols="6">
                  <small class="text-muted d-block">Colis: {{ repo.orders_count }}</small>
                  <strong>{{ repo.collectible_amount_total }} DT</strong>
                </b-col>
              </b-row>
            </b-card>
          </div>

          <!-- Active Vehicles List -->
          <h5 class="mb-3">
            <feather-icon
              icon="TruckIcon"
              size="18"
              class="mr-1"
            />
            Véhicules en service ({{ todayStatistics.overview.active_vehicles_count }})
          </h5>
          <b-card class="mb-4">
            <div
              v-if="todayStatistics.active_vehicles.length > 0"
              class="d-flex flex-wrap"
            >
              <b-badge
                v-for="vehicle in todayStatistics.active_vehicles"
                :key="vehicle"
                variant="light-info"
                class="mr-2 mb-2 p-2"
              >
                <feather-icon
                  icon="TruckIcon"
                  size="14"
                />
                {{ vehicle }}
              </b-badge>
            </div>
            <div
              v-else
              class="text-center text-muted"
            >
              <feather-icon
                icon="TruckIcon"
                size="32"
                class="mb-2 opacity-50"
              />
              <p class="mb-0">
                Aucun véhicule en service
              </p>
            </div>
          </b-card>
        </div>

        <!-- Error State -->
        <div
          v-else
          class="text-center py-5"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="48"
            class="text-danger mb-3"
          />
          <p class="text-muted">
            Erreur lors du chargement des statistiques
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="loadTodayStatistics"
          >
            <feather-icon icon="RefreshCwIcon" />
            Réessayer
          </b-button>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BPagination, BFormGroup, BFormInput, BButton, BTooltip, BBadge,
  BSpinner, BFormSelect, BRow, BCol, BCard, BAvatar, BFormRadioGroup,
  BFormRadio, BFormDatepicker, BSidebar, BProgress, BAlert,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import QrcodeVue from 'qrcode.vue'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BTooltip,
    BButton,
    BBadge,
    BSpinner,
    BFormSelect,
    QrcodeVue,
    vSelect,
    BRow,
    BCol,
    BCard,
    BAvatar,
    BFormRadioGroup,
    BFormRadio,
    BFormDatepicker,
    BSidebar,
    BProgress,
    BAlert,
  },
  data() {
    return {
      size: 80,
      isLoading: false,
      pageLength: 100,
      currentPage: 1,
      count: 0,
      deliveryMans: [],
      repositories: [],
      cars: [],
      user: {},
      selectedRoadmapForValidation: null, // For confirmation modal
      selectedRoadmapForCompletion: null, // For completion confirmation modal
      selectedRoadmapForNonValidation: null, // For non-validation confirmation modal
      untreatedTicketsError: null, // For untreated tickets error display

      // Today's statistics
      showStatisticsSidebar: false,
      todayStatistics: null,
      statisticsLoading: false,

      // Fixed filters object
      filters: {
        id: '',
        delivery_man: null, // Single delivery man selection
        car: null, // Car selection via dropdown
        repository: null,
        is_payed: null,
        is_validated: null,
        created_at_from: null,
        created_at_to: null,
        total_price_min: null,
        total_price_max: null,
        is_completed: null,
      },

      // Applied filters for display
      appliedFilters: [],

      // Date picker configuration
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1, // Monday
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
        labelCalendar: 'Calendrier',
        labelNav: 'Navigation du calendrier',
      },

      columns: [
        {
          label: 'Code',
          field: 'id',
        },
        {
          label: 'Livreur',
          field: 'delivery_man',
        },
        {
          label: 'Dépôt',
          field: 'repository',
        },
        {
          label: 'Véhicule',
          field: 'car',
        },
        {
          label: 'N° Colis',
          field: 'orders_count',
        },
        {
          label: 'Prix Total (DT)',
          field: 'total_price',
        },
        {
          label: 'Statut Paiement',
          field: 'is_payed',
        },
        {
          label: 'Validation',
          field: 'is_validated',
        },
        {
          label: 'Terminé',
          field: 'is_completed',
        },
        {
          label: 'Date | Heure',
          field: 'created_at',
          formatFn: this.formatFn,
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      roadmaps: [],
    }
  },

  computed: {
    /**
     * Check if there are active filters applied
     */
    hasActiveFilters() {
      // Check if any filter has a value
      return Object.keys(this.filters).some(key => {
        const value = this.filters[key]
        if (key === 'delivery_man' || key === 'repository') {
          return value !== null && value !== undefined
        }
        return value !== null && value !== undefined && value !== '' && value !== false
      })
    },

    /**
     * Calculate total paid roadmaps
     */
    totalPaidRoadmaps() {
      return this.roadmaps.filter(roadmap => roadmap.is_payed).length
    },

    /**
     * Calculate total unpaid roadmaps
     */
    totalUnpaidRoadmaps() {
      return this.roadmaps.filter(roadmap => !roadmap.is_payed).length
    },

    /**
     * Calculate total price across all roadmaps
     */
    totalPrice() {
      return this.roadmaps.reduce((sum, roadmap) => sum + parseFloat(roadmap.total_price || 0), 0).toFixed(3)
    },
  },

  created() {
    this.user = storeAuth.state.user
    this.loadInitialData()
  },

  methods: {
    async loadInitialData() {
      const requests = [
        this.fetchRoadmaps(),
        this.getDeliveryMans(),
        this.getCars(),
      ]

      if (this.user.role === 'admin') {
        requests.push(this.getRepositories())
      }

      await Promise.all(requests)
    },

    async getRepositories() {
      try {
        const response = await axios.get('/api/repositories/')
        this.repositories = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      }
    },

    async getDeliveryMans() {
      try {
        const response = await axios.get('api/auth/users/drivers/')
        this.deliveryMans = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching delivery men:', error)
      }
    },

    async getCars() {
      try {
        const response = await axios.get('/api/cars/dropdown/')
        this.cars = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching cars:', error)
      }
    },

    /**
     * Apply all filters and fetch roadmaps
     */
    async applyFilters() {
      this.currentPage = 1
      this.updateAppliedFilters()
      await this.fetchRoadmaps()
    },

    /**
     * Format date for display
     */
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      // Format: DD/MM/YYYY HH:mm
      const day = d.toLocaleDateString('fr-FR')
      const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      return `${day} ${time}`
    },

    /**
     * Format date for API (ensure YYYY-MM-DD format)
     */
    formatDateForAPI(date) {
      if (!date) return ''
      // Bootstrap Vue datepicker returns date in YYYY-MM-DD format
      return date
    },

    /**
     * Fetch roadmaps with current filters - fixed version
     */
    async fetchRoadmaps() {
      this.isLoading = true

      try {
        // Build query parameters from filters
        const params = {
          page: this.currentPage,
          page_size: this.pageLength,
        }

        // Add non-empty filters to params with proper boolean handling
        Object.keys(this.filters).forEach(key => {
          const value = this.filters[key]

          // Handle boolean filters explicitly
          if (key === 'is_payed' || key === 'is_validated') {
            if (value === true) {
              params[key] = 'true'
            } else if (value === false) {
              params[key] = 'false'
            }
            // If value is null, don't add the parameter (show all)
          } else if (value !== null && value !== '' && value !== false) {
            // Handle other filters
            if (key === 'delivery_man') {
              params.delivery_man = value?.id || value
            } else if (key === 'car') {
              params.car = value?.serial_number || value // Use serial_number for car filtering
            } else if (key === 'repository') {
              params.repository = value?.id || value
            } else if (key === 'created_at_from' || key === 'created_at_to') {
              // Format dates for API
              params[key] = this.formatDateForAPI(value)
            } else if (key === 'has_orders' && value === true) {
              params[key] = 'true'
            } else {
              params[key] = value
            }
          }
        })

        const { data } = await axios.get('/api/roadmaps/list/', { params })

        this.roadmaps = data.results
        this.count = data.count
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des run sheets')
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Open confirmation modal before toggling validation
     */
    openValidationConfirm(roadmap) {
      this.selectedRoadmapForValidation = roadmap
      this.$refs.confirmValidationModal.show()
    },

    /**
     * Called when user confirms validation toggle
     */
    async confirmToggleValidation() {
      if (!this.selectedRoadmapForValidation) return
      await this.toggleValidation(this.selectedRoadmapForValidation.id)
      this.selectedRoadmapForValidation = null
    },

    /**
     * Open confirmation modal before toggling completion
     */
    openCompletionConfirm(roadmap) {
      this.selectedRoadmapForCompletion = roadmap
      this.$refs.confirmCompletionModal.show()
    },

    /**
     * Called when user confirms completion toggle
     */
    async confirmToggleCompletion() {
      if (!this.selectedRoadmapForCompletion) return
      await this.toggleCompletion(this.selectedRoadmapForCompletion.id)
      this.selectedRoadmapForCompletion = null
    },

    /**
     * Open confirmation modal before toggling non-validation
     */
    openNonValidationConfirm(roadmap) {
      this.selectedRoadmapForNonValidation = roadmap
      this.$refs.confirmNonValidationModal.show()
    },

    /**
     * Called when user confirms non-validation toggle
     */
    async confirmToggleNonValidation() {
      if (!this.selectedRoadmapForNonValidation) return
      await this.toggleValidation(this.selectedRoadmapForNonValidation.id)
      this.selectedRoadmapForNonValidation = null
    },

    async toggleValidation(roadmapId) {
      try {
        this.isLoading = true

        const response = await axios.post(`/api/roadmaps/toggle-validation/${roadmapId}/`)

        if (response.data.success) {
          // Update the roadmap in the local list
          const roadmapIndex = this.roadmaps.findIndex(r => r.id === roadmapId)
          if (roadmapIndex !== -1) {
            this.roadmaps[roadmapIndex].is_validated = response.data.is_validated
            this.roadmaps[roadmapIndex].validated_by = response.data.validated_by
            this.roadmaps[roadmapIndex].validated_at = response.data.validated_at
          }

          this.showToast('success', 'top-center', response.data.message)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling validation:', error)
        this.showToast('danger', 'top-center', 'Erreur lors de la validation')
      } finally {
        this.isLoading = false
      }
    },

    async toggleCompletion(roadmapId) {
      try {
        this.isLoading = true

        const response = await axios.post(`/api/roadmaps/toggle-completion/${roadmapId}/`)

        if (response.data.success) {
          // Update the roadmap in the local list
          const roadmapIndex = this.roadmaps.findIndex(r => r.id === roadmapId)
          if (roadmapIndex !== -1) {
            this.roadmaps[roadmapIndex].is_completed = response.data.is_completed
            this.roadmaps[roadmapIndex].completed_by = response.data.completed_by
            this.roadmaps[roadmapIndex].completed_at = response.data.completed_at
          }

          this.showToast('success', 'top-center', response.data.message)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling completion:', error)

        // Handle untreated driver tickets error
        if (error.response && error.response.status === 400 && error.response.data) {
          const errorData = error.response.data

          if (errorData.untreated_tickets_count > 0) {
            // Show detailed error with untreated tickets info
            this.showUntreatedTicketsError(roadmapId, errorData)
          } else {
            // Generic error message
            this.showToast('danger', 'top-center', errorData.error || errorData.detail || 'Erreur lors du changement de statut')
          }
        } else {
          this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
        }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Show error modal for untreated driver tickets
     */
    showUntreatedTicketsError(roadmapId, errorData) {
      const ticketCount = errorData.untreated_tickets_count
      const tickets = errorData.untreated_tickets || []
      const detail = errorData.detail || errorData.error

      // Create table rows for tickets
      const ticketRows = tickets.map(ticket => this.$createElement('tr', {}, [
        this.$createElement('td', { class: 'text-center' }, `#${ticket.id}`),
        this.$createElement('td', { class: 'text-center' }, `#${ticket.order_id}`),
        this.$createElement('td', {}, ticket.reason),
        this.$createElement('td', { class: 'text-center' }, [
          this.$createElement('b-badge', {
            props: {
              variant: ticket.status_code === 'resolved' ? 'info' : 'warning',
            },
          }, ticket.status),
        ]),
        this.$createElement('td', {}, ticket.driver),
      ]))

      this.$bvModal.msgBoxOk(
        [
          this.$createElement('div', { class: 'text-center mb-3' }, [
            this.$createElement('feather-icon', {
              props: { icon: 'AlertTriangleIcon', size: '48' },
              class: 'text-warning mb-2',
            }),
          ]),
          this.$createElement('h5', { class: 'font-weight-bold mb-3' }, 'Réclamations livreur non traitées'),
          this.$createElement('p', { class: 'text-muted mb-3' }, detail),
          this.$createElement('div', { class: 'alert alert-warning mb-3' }, [
            this.$createElement('strong', {}, `${ticketCount} réclamation(s) non traitée(s)`),
          ]),

          // Tickets table
          this.$createElement('div', { class: 'table-responsive mb-3' }, [
            this.$createElement('table', { class: 'table table-sm table-bordered' }, [
              this.$createElement('thead', { class: 'thead-light' }, [
                this.$createElement('tr', {}, [
                  this.$createElement('th', { class: 'text-center' }, 'Ticket'),
                  this.$createElement('th', { class: 'text-center' }, 'Commande'),
                  this.$createElement('th', {}, 'Motif'),
                  this.$createElement('th', { class: 'text-center' }, 'Statut'),
                  this.$createElement('th', {}, 'Livreur'),
                ]),
              ]),
              this.$createElement('tbody', {}, ticketRows),
            ]),
          ]),

          this.$createElement('p', { class: 'text-info mb-2' }, [
            this.$createElement('strong', {}, 'Actions requises:'),
          ]),
          this.$createElement('ul', { class: 'text-left small mb-0' }, [
            this.$createElement('li', {}, 'Toutes les réclamations doivent être fermées ("Fermé") ou escaladées ("Escaladé")'),
            this.$createElement('li', {}, 'Les réclamations avec le statut "Résolu" doivent être fermées'),
          ]),
        ],
        {
          title: 'Impossible de marquer comme terminé',
          size: 'lg',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: 'Fermer',
          headerClass: 'p-2 border-bottom-0',
          footerClass: 'p-2 border-top-0',
          centered: true,
        },
      )
    },

    /**
     * Update the applied filters display
     */
    updateAppliedFilters() {
      this.appliedFilters = []

      // Check each filter and add to appliedFilters if it has a value
      Object.keys(this.filters).forEach(key => {
        const value = this.filters[key]
        let label = key
        let displayValue = value

        // Skip empty values
        if (value === null || value === undefined || value === '' || value === false) {
          return
        }

        // Format specific filter types
        switch (key) {
          case 'delivery_man':
            if (value && value.username) {
              label = 'Livreur'
              displayValue = value.username
            }
            break
          case 'repository':
            if (value && value.name) {
              label = 'Dépôt'
              displayValue = value.name
            }
            break
          case 'created_at_from':
            label = 'Date début'
            displayValue = this.formatDate(value)
            break
          case 'created_at_to':
            label = 'Date fin'
            displayValue = this.formatDate(value)
            break
          case 'is_payed':
            label = 'Statut paiement'
            displayValue = value ? 'Payé' : 'Non payé'
            break
          case 'is_validated':
            label = 'Statut validation'
            displayValue = value ? 'Validé' : 'Non validé'
            break
          default:
            // Use the key as label for other filters
            label = this.formatFilterLabel(key)
        }

        // Only add if we have a display value
        if (displayValue) {
          this.appliedFilters.push({
            key,
            label,
            value: displayValue,
          })
        }
      })
    },

    /**
     * Format filter labels for display
     */
    formatFilterLabel(key) {
      const labelMap = {
        date_from: 'Date début',
        date_to: 'Date fin',
        is_payed: 'Payé',
        is_validated: 'Validé',
      }
      return labelMap[key] || key
    },

    /**
     * Clear all filters and refresh data
     */
    clearAllFilters() {
      // Reset all filters to their default values
      this.filters = {
        id: '',
        delivery_man: null,
        car: null,
        repository: null,
        is_payed: null,
        is_validated: null,
        created_at_from: null,
        created_at_to: null,
        total_price_min: null,
        total_price_max: null,
      }

      // Reset current page and refresh data
      this.currentPage = 1
      this.appliedFilters = []
      this.fetchRoadmaps()
    },

    /**
     * Remove a specific filter
     */
    removeFilter(filterKey) {
      if (filterKey === 'delivery_man' || filterKey === 'repository') {
        this.filters[filterKey] = null
      } else {
        this.filters[filterKey] = null
      }
      this.applyFilters()
    },

    /**
     * Reset filters - legacy method
     */
    resetFilters() {
      this.clearAllFilters()
      this.showToast('success', 'top-right', 'Filtres réinitialisés')
    },

    /**
     * Handle pagination
     */
    async pageNext(currentPage) {
      this.currentPage = currentPage
      await this.fetchRoadmaps()
    },

    /**
     * Show toast notification
     */
    showToast(variant, _position, text) {
      // Always show toast at top-right
      this.$toast(text, {
        type: variant,
        position: 'top-right',
        timeout: 4000,
        closeOnClick: true,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        draggable: true,
        draggablePercent: 0.6,
        showCloseButtonOnHover: false,
        hideProgressBar: false,
        closeButton: 'button',
        icon: true,
        rtl: false,
      })
    },

    /**
     * Open today's statistics sidebar
     */
    async openTodayStatistics() {
      this.showStatisticsSidebar = true
      await this.loadTodayStatistics()
    },

    /**
     * Load today's statistics from API
     */
    async loadTodayStatistics() {
      this.statisticsLoading = true
      try {
        const { data } = await axios.get('/api/roadmaps/today-statistics/')
        this.todayStatistics = data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading today statistics:', error)
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des statistiques')
      } finally {
        this.statisticsLoading = false
      }
    },

    /**
     * Format statistics date
     */
    formatStatisticsDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.95rem;
  border: 1px solid transparent;
  letter-spacing: 0.1px;
  text-transform: capitalize;
  box-shadow: none;
}

.mr-50 {
  margin-right: 0.35rem;
}

.pill-paid {
  background: rgba(46, 204, 113, 0.12);
  color: #1f8a4c;
  border-color: #b8e6cb;
}

.pill-unpaid {
  background: rgba(244, 180, 0, 0.12);
  color: #b57a00;
  border-color: #f7d88a;
}

.pill-validated {
  background: rgba(14, 165, 233, 0.12);
  color: #0b7bb5;
  border-color: #b9e6ff;
}

.pill-unvalidated {
  background: rgba(156, 163, 175, 0.12);
  color: #4b5563;
  border-color: #e5e7eb;
}

.pill-completed {
  background: rgba(16, 185, 129, 0.14);
  color: #0f8a63;
  border-color: #b2ead7;
}

.pill-progress {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
  border-color: #c7dbff;
}

.pill-warning {
  background: rgba(239, 68, 68, 0.14);
  color: #b42318;
  border-color: #f9c2c2;
  box-shadow: 0 6px 14px rgba(239, 68, 68, 0.22);
}

.btn-black {
  background: #1d1d1b !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    background: #0f0f0e !important;
    color: #ffffff !important;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25) !important;
  }
}

.applied-filters {
  .badge {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }
}

// Enhanced styling for no results card
.no-results-card {
  border: 2px dashed #e3e6f0;
  background-color: #f8f9fc;

  .feather-icon {
    opacity: 0.5;
  }
}

// Table styling improvements
.vue-good-table {
  .font-weight-semibold {
    font-weight: 600;
  }

  .text-right {
    text-align: right;
  }

  .text-center {
    text-align: center;
  }

  // QR Code styling
  .qr-code {
    border: 1px solid #e3e6f0;
    border-radius: 4px;
    padding: 2px;
    background: white;
  }
}

// Pagination improvements
.pagination {
  .page-link {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
}

// Statistics Sidebar Styling
.stat-card {
  transition: all 0.3s ease;
  border-width: 2px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);
  }
}

.b-sidebar {
  .b-sidebar-body {
    overflow-y: auto;
    max-height: calc(100vh - 80px);
  }
}

.opacity-50 {
  opacity: 0.5;
}
</style>
